import { z } from 'zod'

const STORAGE_KEY = 'pharme_mailjet_campaign'
const LANDING_KEY = 'pharme_mailjet_campaign_landing'

const attributionSchema = z.object({
  campaign: z.string().min(1).max(255),
  account: z.string().max(60).optional(),
  source: z.literal('Mailjet'),
  medium: z.literal('email'),
})

const eventSchema = z.object({
  eventId: z.string().min(8).max(100),
  event: z.enum([
    'campaign_landing',
    'campaign_add_to_cart',
    'campaign_begin_checkout',
    'campaign_purchase',
    'campaign_conversion',
  ]),
  application: z.literal('store'),
  campaign: z.string().min(1).max(255),
  account: z.string().max(60).optional(),
  source: z.literal('Mailjet'),
  medium: z.literal('email'),
  landingPage: z.string().max(500).optional(),
  orderReference: z.string().max(100).optional(),
  amount: z.number().nonnegative().max(10_000_000).optional(),
  currency: z.string().length(3).optional(),
  occurredAt: z.string().datetime(),
})

export type MailjetCampaignAttribution = z.infer<typeof attributionSchema>

const eventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `event_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

const accountFromCampaign = (campaign: string): string | undefined =>
  campaign.match(/^\[([a-z0-9_-]+)]/i)?.[1]

export const getMailjetCampaignAttribution = (): MailjetCampaignAttribution | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    return raw ? attributionSchema.parse(JSON.parse(raw)) : null
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

const sendEvent = (payload: z.infer<typeof eventSchema>): void => {
  const parsed = eventSchema.safeParse(payload)
  if (!parsed.success) return
  void fetch('/api/mailjetCampaignEvent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(parsed.data),
    keepalive: true,
  }).catch(() => undefined)
}

export const captureMailjetCampaignLanding = (url: string): void => {
  if (typeof window === 'undefined') return
  const parsedUrl = new URL(url, window.location.origin)
  const source = parsedUrl.searchParams.get('utm_source')
  const medium = parsedUrl.searchParams.get('utm_medium')
  const campaign = parsedUrl.searchParams.get('utm_campaign')?.trim()
  if (source?.toLowerCase() !== 'mailjet' || medium?.toLowerCase() !== 'email' || !campaign) {
    return
  }

  const attribution = attributionSchema.parse({
    campaign,
    account: accountFromCampaign(campaign),
    source: 'Mailjet',
    medium: 'email',
  })
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))

  const landingKey = `${campaign}|${parsedUrl.pathname}`
  if (window.sessionStorage.getItem(LANDING_KEY) === landingKey) return
  window.sessionStorage.setItem(LANDING_KEY, landingKey)
  sendEvent({
    eventId: eventId(),
    event: 'campaign_landing',
    application: 'store',
    ...attribution,
    landingPage: parsedUrl.pathname,
    occurredAt: new Date().toISOString(),
  })
}

export const trackMailjetCampaignEvent = (
  event: 'campaign_add_to_cart' | 'campaign_begin_checkout' | 'campaign_conversion',
  details: { landingPage?: string; amount?: number; currency?: string } = {}
): void => {
  const attribution = getMailjetCampaignAttribution()
  if (!attribution) return
  sendEvent({
    eventId: eventId(),
    event,
    application: 'store',
    ...attribution,
    ...details,
    occurredAt: new Date().toISOString(),
  })
}
